import UncontrolledPopover from 'components/SafeUncontrolledPopover';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import {
  Button,
  Card,
  CardBody,
  Col,
  ListGroup,
  ListGroupItem,
  Row,
} from 'reactstrap';
import { useAuthProvider } from 'utils/api/useAuthProvider';
import { DEVELOPMENT_PLANS_BASE_URL_PATH } from 'utils/models/DevelopmentPlan';

import DevelopmentPlansDashboard from 'views/DevelopmentPlans/DevelopmentPlansDashboard';
import * as consts from '../../consts/consts';
import {
  ExtendedPerson,
  Features,
  Me,
  Organization,
  Person,
} from '../../types';
import ConfirmAPI from '../../utils/api/ConfirmAPI';
import { OBJECTIVE_VIEW_TYPES } from '../../utils/models/Objective';
import {
  getPersonDisplayTitleHtml,
  isValidPersonId,
  isValidSlug,
  peopleIdsAreEqual,
} from '../../utils/models/Person';
import {
  activitiesAreEnabled,
  atLeastOneContinuousFeedbackFeatureIsEnabled,
  goalsAreEnabled,
  objectivesAreEnabled,
  oneOnOnesAreEnabled,
  operatingManualsAreEnabled,
  performanceProfilesAreEnabled,
  resumesAreEnabled,
  useFeatures,
} from '../../utils/util/features';
import { loadOrRender } from '../../utils/util/formatter';
import ModalActivityEditorButton from '../Activities/ModalActivityEditorButton';
import SurveysAndPerformancePage from '../CustomSurveys/SurveysAndPerformancePage';
import ModalFeedbackEditorButton from '../Feedback/ModalFeedbackEditorButton';
import Page404 from '../Layout/Pages/Errors/Page404';
import Page from '../Layout/Pages/Page';
import ObjectivesTeamNav from '../Objectives/ObjectivesTeamNav';
import OneOnOnesDirectory from '../OneOnOnes/OneOnOnesDirectory';
import EmptyState from '../Widgets/EmptyState';
import SwitchInput from '../Widgets/Inputs/SwitchInput';
import Loading from '../Widgets/Loading';
import ModalAspirationEditor from '../Widgets/Modals/ModalAspirationEditor';
import ManualCopyUrlButton from './ManualCopyUrlButton';
import PersonalObjectives from './PersonalObjectives';
import PersonDashboard from './PersonDashboard';
import PersonFeedbackList from './PersonFeedbackList';
import PersonGoals from './PersonGoals';
import PersonOneOnOnes from './PersonOneOnOnes';
import PersonOperatingManual from './PersonOperatingManual';

const getTabHeader = (name, icon) => {
  return (
    <>
      <span className={'d-lg-none display-4 me-n1 ' + icon}></span>
      <span className="d-none d-lg-inline">{name}</span>
    </>
  );
};

type Props = {
  personId?: number;
  currentOrganization: Organization;
  me: Me;
  features: Features;
  currentProxyPerson: Person;
};

const PersonProfile: FC<Props> = (props: Props) => {
  const { formatMessage } = useIntl();
  const { isGroupAndOptionEnabled } = useFeatures();
  const location = useLocation();
  const history = useHistory();
  const [isMounted, setIsMounted] = useState(false);
  const [createActivityIsOpen, setCreateActivityIsOpen] = useState(false);
  const [activityType, setActivityType] = useState(null);
  const [contributions, setContributions] = useState([]);
  const [filterName, setFilterName] = useState('');

  // default to showing manager-only details for discoverability
  const [
    showManagerOnlyPerformanceDetails,
    setShowManagerOnlyPerformanceDetails,
  ] = useState(true);

  const [showSentFeedback, setShowSentFeedback] = useState(false);
  const [aspirations, setAspirations] = useState(undefined);
  const [aspirationCreateModalIsOpen, setAspirationCreateModalIsOpen] =
    useState(false);
  const toggleAspirationCreateModal = useCallback(
    () => setAspirationCreateModalIsOpen(!aspirationCreateModalIsOpen),
    [aspirationCreateModalIsOpen]
  );

  useEffect(() => {
    setIsMounted(true);
    return () => {
      setIsMounted(false);
    };
  }, []);

  const openCreateActivityDialog = useCallback((type) => {
    setActivityType(type);
    setCreateActivityIsOpen(true);
  }, []);

  const onCreateActivityDialogClosed = useCallback(() => {
    // clear activity type so reopening allows picking new type
    setActivityType(null);
    setCreateActivityIsOpen(false);
  }, []);

  const params = useParams();
  // @ts-expect-error
  const personId = props.personId ? props.personId : params.personId;
  // @ts-expect-error
  const personSlug = params.slug;
  const validIdentifier = isValidSlug(personSlug) && isValidPersonId(personId);
  const meId = props.me?.id;
  const isMe =
    peopleIdsAreEqual(meId, personId) ||
    (!!personSlug &&
      props.me?.url?.toString() === '/' + personSlug?.toString());
  const [errorMessage, setErrorMessage] = useState(null);
  const [person, setPerson] = useState<ExtendedPerson | undefined>(undefined);
  const [feedbackErrorMessage, setFeedbackErrorMessage] = useState(null);
  const [feedbackList, setFeedbackList] = useState(undefined);
  const { user } = useAuthProvider();

  const userSub = user?.sub;

  const urlPrefix = personSlug
    ? '/' + personSlug
    : personId
      ? consts.PEOPLE_SUBROUTE().path + '/' + personId
      : null;

  const noTabProvidedInUrl = useMemo(
    () => location?.pathname === urlPrefix,
    [location?.pathname, urlPrefix]
  );

  const activitiesEnabled = useMemo(
    () => activitiesAreEnabled(props.features),
    [props.features]
  );

  const resumesEnabled = useMemo(
    () => resumesAreEnabled(props.features),
    [props.features]
  );

  // Determine if we are on the "resume" tab
  const isResumeTab = noTabProvidedInUrl && resumesEnabled;

  const setAndCachePerson = useCallback(
    (p) => {
      // cache so fetching page next time has latest data from cache
      ConfirmAPI.setObjectInCache(
        userSub,
        props.currentProxyPerson,
        ConfirmAPI.OBJECT_TYPES.PEOPLE,
        p?.id,
        p
      );

      setPerson(p);
    },
    [props.currentProxyPerson, userSub]
  );

  useEffect(() => {
    if (!isMounted) {
      return;
    }

    // fetch person's details
    if (
      userSub &&
      typeof person === 'undefined' &&
      (personSlug || personId) &&
      validIdentifier
    ) {
      const queryParams = personSlug ? { slug: 'true' } : undefined;

      ConfirmAPI.getObject(
        userSub,
        props.currentProxyPerson,
        ConfirmAPI.OBJECT_TYPES.PEOPLE,
        personSlug || personId,
        (data) => {
          if (isMounted) {
            const newPerson = data || null;
            setPerson(newPerson);
            // don't add self to invitation list for creating new contributions, but
            // add the current person whose profile you are on
            if (!isMe && newPerson) {
              // @ts-expect-error
              setContributions([newPerson]);
            }
          }
        },
        (message) => {
          // TODO: don't hardcode based on error message, use status code
          if (message === 'Not Found') {
            // since slugs may return a not found error, just show not found page in this case
            return <Page404 />;
          } else {
            setErrorMessage(message);
          }
        },
        queryParams
      );
    }
  }, [
    isMe,
    isMounted,
    person,
    personId,
    personSlug,
    props.currentProxyPerson,
    userSub,
    validIdentifier,
  ]);

  const isAdminable = useMemo(() => person?.is_adminable ?? false, [person]);

  const resumesFeatureIsEnabled = resumesAreEnabled(props.features);
  const oneOnOnesFeatureIsEnabled = oneOnOnesAreEnabled(props.features);
  const objectivesFeatureIsEnabled = objectivesAreEnabled(props.features);
  const goalsFeatureIsEnabled = goalsAreEnabled(props.features);
  const operatingManualFeatureIsEnabled = operatingManualsAreEnabled(
    props.features
  );

  const continuousFeedbackFeatureIsEnabled = useMemo(
    () => atLeastOneContinuousFeedbackFeatureIsEnabled(props.features),
    [props.features]
  );

  useEffect(() => {
    if (!isMounted || !continuousFeedbackFeatureIsEnabled || !person) {
      return;
    }

    const params = {
      person: person.id,
      organization: props.currentOrganization?.id,
      proxy: props.currentProxyPerson
        ? props.currentProxyPerson.email
        : undefined,
      manager_view: showManagerOnlyPerformanceDetails,
    };

    ConfirmAPI.getUrlWithCache(
      '/feedback',
      // @ts-expect-error
      null,
      null, // this is too much data, don't cache it
      null,
      params,
      (data) => {
        if (isMounted) {
          if (data?.results) {
            setFeedbackList(data.results);
          }
        }
      },
      (message) => {
        setFeedbackErrorMessage(message);
      }
    );
  }, [
    isMounted,
    props.currentOrganization?.id,
    props.currentProxyPerson,
    showManagerOnlyPerformanceDetails,
    continuousFeedbackFeatureIsEnabled,
    person,
  ]);

  const isBelowInChainOfCommand = useMemo(
    () =>
      person?.is_below_in_chain_of_command ||
      person?.is_direct_report_in_campaign,
    [person?.is_below_in_chain_of_command, person?.is_direct_report_in_campaign]
  );

  const showPerformance = useMemo(
    () =>
      // note: we still show performance tab if !person to prevent the flicker of a 404 page
      // when visiting /performance directly (once the person loads, it will go to the
      // 404 page if necessary)
      performanceProfilesAreEnabled(props.features) &&
      (isMe || !person || isBelowInChainOfCommand || isAdminable),
    [isAdminable, isBelowInChainOfCommand, isMe, person, props.features]
  );

  const showDevelopmentPlans = useMemo(
    () =>
      props.features?.development_plans?.enabled &&
      // note: we still show performance tab if !person to prevent the flicker of a 404 page
      // when visiting /performance directly (once the person loads, it will go to the
      // 404 page if necessary)
      (isMe || !person || isBelowInChainOfCommand || isAdminable),
    [
      isAdminable,
      isBelowInChainOfCommand,
      isMe,
      person,
      props.features?.development_plans?.enabled,
    ]
  );

  useEffect(() => {
    // Only allow if user can see performance, but is not the user
    // and querystring exists
    if (isMe) {
      setShowManagerOnlyPerformanceDetails(false);
    } else {
      if (showPerformance && location.search) {
        const pageConfigs = new URLSearchParams(location.search);
        if (pageConfigs?.get('manager') === 'false') {
          setShowManagerOnlyPerformanceDetails(false);
        }
      }
    }
  }, [location, isMe, showPerformance]);

  const subtitle = useMemo(() => {
    if (!person) {
      return '';
    }
    // construct subtitle based on information populated (or not) by user
    const titleInsert = getPersonDisplayTitleHtml(formatMessage, person);
    const locationInsert = person.location
      ? formatMessage(
          {
            id: 'app.views.person.person_profile.location',
            defaultMessage:
              '{withTitle, select, true { in {location}} other { Located in {location}}}',
          },
          {
            location: person.location,
            withTitle: !!titleInsert,
          }
        )
      : '';

    // return additional information related to profile / operating manual
    return person.is_active ? (
      <>
        <div>
          {(titleInsert || locationInsert) && (
            <span className="me-2">
              {titleInsert}
              {locationInsert}
            </span>
          )}
          {isMe && (
            <>
              {isResumeTab && (
                <>
                  <ModalActivityEditorButton
                    buttonClassName="d-block d-sm-none"
                    buttonText={formatMessage({
                      id: 'app.views.person.person_profile.add_to_my_resume',
                      defaultMessage: 'Add to my resume',
                    })}
                    activityType={activityType}
                    contributions={contributions}
                    isOpen={createActivityIsOpen}
                    onClosed={onCreateActivityDialogClosed}
                  />
                </>
              )}
            </>
          )}
        </div>
      </>
    ) : (
      <span className="fst-italic">
        {consts.FORMER_EMPLOYEE_WORDING(formatMessage)}
      </span>
    );
  }, [
    activityType,
    contributions,
    createActivityIsOpen,
    isMe,
    isResumeTab,
    onCreateActivityDialogClosed,
    person,
    formatMessage,
  ]);

  const renderTeamNav = useCallback(
    (person, currentFirstDayOfTimeFrame, firstDay, lastDay) => (
      <ObjectivesTeamNav
        person={person}
        currentFirstDayOfTimeFrame={currentFirstDayOfTimeFrame}
        firstDay={firstDay}
        lastDay={lastDay}
      />
    ),
    []
  );

  // Construct action buttons for feedback and performance tab
  const getTabActions = useCallback(
    (isManager, showFeedbackButton) => (
      <Row xs="1" sm="12" className="align-items-center">
        {isManager && (
          <Col className="col-auto me-sm-3 mb-3 mb-md-0">
            <SwitchInput
              switchLabel={formatMessage({
                id: 'app.views.person.person_profile.show_manager_view',
                defaultMessage: 'Show manager view',
              })}
              value={showManagerOnlyPerformanceDetails}
              onChange={setShowManagerOnlyPerformanceDetails}
            />
          </Col>
        )}
        {showFeedbackButton && continuousFeedbackFeatureIsEnabled && (
          <Col className="col-sm-auto mb-3 mb-md-0 pe-0">
            <ModalFeedbackEditorButton
              buttonText={formatMessage({
                id: 'app.views.person.person_profile.add_feedback_or_notes',
                defaultMessage: 'Add feedback or notes',
              })}
            />
          </Col>
        )}
        <Col className="col-sm-auto mb-3 mb-md-0">
          <Button color="light" onClick={() => window.print()}>
            <i className="me-2 fe fe-upload" />
            <FormattedMessage
              id="app.views.person.person_profile.export"
              defaultMessage="Export"
            />
          </Button>
        </Col>
      </Row>
    ),
    [
      continuousFeedbackFeatureIsEnabled,
      showManagerOnlyPerformanceDetails,
      formatMessage,
    ]
  );

  const tabs = useMemo(() => {
    const tabs = [];

    if (urlPrefix) {
      if (resumesFeatureIsEnabled) {
        // @ts-expect-error
        tabs.push({
          path: urlPrefix,
          name: getTabHeader(
            formatMessage({
              id: 'app.views.person.person_profile.overview_tab_heading',
              defaultMessage: 'Overview',
            }),
            consts.ICONS.RESUME
          ),
          action: isMe ? (
            activitiesEnabled ? (
              <ModalActivityEditorButton
                buttonClassName="d-none d-sm-block"
                buttonText={formatMessage({
                  id: 'app.views.person.person_profile.add_to_my_resume',
                  defaultMessage: 'Add to my resume',
                })}
                activityType={activityType}
                contributions={contributions}
                isOpen={createActivityIsOpen}
                onClosed={onCreateActivityDialogClosed}
              />
            ) : (
              <></>
            )
          ) : (
            continuousFeedbackFeatureIsEnabled && (
              <ModalFeedbackEditorButton
                buttonText={formatMessage({
                  id: 'app.views.person.person_profile.add_feedback_or_notes',
                  defaultMessage: 'Add feedback or notes',
                })}
                feedback={{
                  subject_people: [person],
                }}
              />
            )
          ),
          content: (
            <PersonDashboard
              isMe={isMe}
              // @ts-expect-error
              person={person}
              showPerformance={showPerformance}
              setPerson={setAndCachePerson}
              openCreateActivityDialog={openCreateActivityDialog}
              setCreateActivityIsOpen={setCreateActivityIsOpen}
              filterName={filterName}
              setFilterName={setFilterName}
              feedbackList={feedbackList}
            />
          ),
        });
      }

      if (oneOnOnesFeatureIsEnabled) {
        // @ts-expect-error
        tabs.push({
          path: urlPrefix + consts.PROFILE_TAB_ONE_ON_ONES(formatMessage).path,
          name: getTabHeader(
            formatMessage({
              id: 'app.views.person.person_profile.one_on_ones_tab_heading',
              defaultMessage: '1:1s',
            }),
            consts.ICONS.ONE_ON_ONES
          ),
          content: isMe ? (
            <OneOnOnesDirectory inMyProfile={true} />
          ) : (
            // @ts-expect-error
            <PersonOneOnOnes person={person} />
          ),
        });
      }

      // you can write OKRs for people who haven't yet created an account
      if (objectivesFeatureIsEnabled) {
        // @ts-expect-error
        tabs.push({
          path: urlPrefix + consts.OBJECTIVES(formatMessage).path,
          name: getTabHeader(
            formatMessage({
              id: 'app.views.person.person_profile.objectives_tab_heading',
              defaultMessage: 'Objectives',
            }),
            consts.ICONS.OBJECTIVE
          ),
          content: (
            <>
              {person && (
                <PersonalObjectives
                  renderTeamNav={renderTeamNav}
                  showTeamNav={true}
                  person={person}
                  viewType={OBJECTIVE_VIEW_TYPES.PERSON_COLLABORATING}
                />
              )}
            </>
          ),
        });
      }

      // the next tabs are meant for people with accounts
      if (goalsFeatureIsEnabled) {
        // @ts-expect-error
        tabs.push({
          path: urlPrefix + '/goals',
          name: getTabHeader(
            formatMessage({
              id: 'app.views.person.person_profile.aspirations_tab_heading',
              defaultMessage: 'Aspirations',
            }),
            consts.ICONS.GOAL
          ),
          action: isMe && (
            <>
              <ModalAspirationEditor
                isOpen={aspirationCreateModalIsOpen}
                toggle={toggleAspirationCreateModal}
                title={formatMessage({
                  id: 'app.views.person.person_profile.title.add_aspiration',
                  defaultMessage: 'Add aspiration',
                })}
                // @ts-expect-error
                callback={(a) => setAspirations([a, ...aspirations])}
              />
              <Button color="primary" onClick={toggleAspirationCreateModal}>
                <FormattedMessage
                  id="app.views.person.person_profile.add_aspiration"
                  defaultMessage="Add aspiration"
                />
              </Button>
            </>
          ),
          content: (
            <>
              {person && (
                <PersonGoals
                  person={person}
                  aspirations={aspirations}
                  setAspirations={setAspirations}
                  aspirationCreateModalIsOpen={aspirationCreateModalIsOpen}
                  toggleAspirationCreateModal={toggleAspirationCreateModal}
                  setPerson={setPerson}
                />
              )}
            </>
          ),
        });
      }

      if (operatingManualFeatureIsEnabled) {
        // @ts-expect-error
        tabs.push({
          path: urlPrefix + '/manual',
          name: getTabHeader(
            formatMessage({
              id: 'app.views.person.person_profile.manual_tab_heading',
              defaultMessage: 'Manual',
            }),
            consts.ICONS.OPERATING_MANUAL
          ),
          action: isMe && (
            <ManualCopyUrlButton
              path={urlPrefix + '/manual'}
            ></ManualCopyUrlButton>
          ),
          content: (
            <>
              {person && (
                <PersonOperatingManual
                  isMe={isMe}
                  setPerson={setAndCachePerson}
                  fullName={person?.full_name}
                  // ensure that {} is there in case someone's profile has a null operating manual
                  operatingManual={person?.profile?.operating_manual || {}}
                  personId={person?.id}
                />
              )}
            </>
          ),
        });
      }

      if (continuousFeedbackFeatureIsEnabled) {
        // @ts-expect-error
        tabs.push({
          path: urlPrefix + '/feedback',
          name: getTabHeader(
            <>
              <div>
                <i
                  id="feedback-tab-lock-icon"
                  className="fe fe-lock me-2"
                  style={{ lineHeight: 1 }}
                />
                <FormattedMessage
                  id="app.views.person.person_profile.feedback_and_notes"
                  defaultMessage="Feedback & notes"
                />
              </div>
              <UncontrolledPopover
                placement="right"
                trigger="hover"
                target="feedback-tab-lock-icon"
              >
                <FormattedMessage
                  id="app.views.person.person_profile.only_manager_and_above_can_see_feedback_information"
                  defaultMessage="Only manager and above (and HR administrators) can see feedback information."
                />
              </UncontrolledPopover>
            </>,
            consts.ICONS.FEEDBACK
          ),
          action: getTabActions(!isMe && person, true),
          content: (
            <>
              {person && (
                <Row className="justify-content-center">
                  <Col className="col-12 col-md-2 pt-0 pt-md-6">
                    <ListGroup className="list-group-flush">
                      <ListGroupItem
                        role="button"
                        className={showSentFeedback ? '' : 'fw-bold'}
                        onClick={() => setShowSentFeedback(false)}
                      >
                        <FormattedMessage
                          id="app.views.person.person_profile.received"
                          defaultMessage="Received"
                        />
                      </ListGroupItem>
                      <ListGroupItem
                        role="button"
                        className={showSentFeedback ? 'fw-bold' : ''}
                        onClick={() => setShowSentFeedback(true)}
                      >
                        <FormattedMessage
                          id="app.views.person.person_profile.sent"
                          defaultMessage="Sent"
                        />
                      </ListGroupItem>
                      {isMe && (
                        <ListGroupItem>
                          <Link
                            target="_blank"
                            rel="noopener noreferrer"
                            to="/feedback/sent"
                          >
                            <FormattedMessage
                              id="app.views.person.person_profile.view_sent_requests"
                              defaultMessage="View requests"
                            />{' '}
                            <i className="fe fe-external-link" />
                          </Link>
                        </ListGroupItem>
                      )}
                    </ListGroup>
                  </Col>
                  <Col className="col-12 col-md-10">
                    <PersonFeedbackList
                      person={person}
                      feedbackList={feedbackList}
                      errorMessage={feedbackErrorMessage}
                      isAdminable={isAdminable}
                      showManagerOnlyPerformanceDetails={
                        showManagerOnlyPerformanceDetails
                      }
                      showSentFeedback={showSentFeedback}
                    />
                  </Col>
                </Row>
              )}
            </>
          ),
        });
      }

      if (showPerformance) {
        // @ts-expect-error
        tabs.push({
          path: urlPrefix + '/performance',
          name: getTabHeader(
            <>
              <div>
                <i
                  id="perf-tab-lock-icon"
                  className="fe fe-lock me-2"
                  style={{ lineHeight: 1 }}
                />
                <FormattedMessage
                  id="app.views.person.person_profile.surveys_and_performance"
                  defaultMessage="Surveys & performance"
                />
              </div>
              <UncontrolledPopover
                placement="right"
                trigger="hover"
                target="perf-tab-lock-icon"
              >
                <FormattedMessage
                  id="app.views.person.person_profile.only_manager_and_above_can_see_performance_information"
                  defaultMessage="Only manager and above (and HR administrators) can see performance information."
                />
              </UncontrolledPopover>
            </>,
            consts.ICONS.PERFORMANCE
          ),
          content: (
            <>
              {person ? (
                <SurveysAndPerformancePage
                  actions={getTabActions(!isMe && person, false)}
                  person={person}
                  isAdminable={isAdminable}
                  showManagerOnlyPerformanceDetails={
                    showManagerOnlyPerformanceDetails
                  }
                />
              ) : null}
            </>
          ),
        });
      }

      if (showDevelopmentPlans) {
        // @ts-expect-error
        tabs.push({
          path: `${urlPrefix}/${DEVELOPMENT_PLANS_BASE_URL_PATH}`,
          name: getTabHeader(
            <>
              <div>
                <i
                  id="plans-tab-lock-icon"
                  className="fe fe-lock me-2"
                  style={{ lineHeight: 1 }}
                />
                <FormattedMessage
                  id="app.views.person.person_profile.development_plans"
                  defaultMessage="Plans"
                />
              </div>
              <UncontrolledPopover
                placement="right"
                trigger="hover"
                target="plans-tab-lock-icon"
              >
                <FormattedMessage
                  id="app.views.person.person_profile.only_manager_and_above_can_see_developent_plans_information"
                  defaultMessage="Only manager and above (and HR administrators) can see development plans information."
                />
              </UncontrolledPopover>
            </>,
            consts.ICONS.DEVELOPMENT_PLAN
          ),
          content: (
            <>
              {person && (
                <>
                  <DevelopmentPlansDashboard
                    embedded={true}
                    person={person}
                    hideHeaderControls={true}
                  />
                </>
              )}
            </>
          ),
        });
      }
    }

    return tabs;
  }, [
    urlPrefix,
    resumesFeatureIsEnabled,
    oneOnOnesFeatureIsEnabled,
    objectivesFeatureIsEnabled,
    goalsFeatureIsEnabled,
    operatingManualFeatureIsEnabled,
    continuousFeedbackFeatureIsEnabled,
    showPerformance,
    showDevelopmentPlans,
    formatMessage,
    isMe,
    activitiesEnabled,
    activityType,
    contributions,
    createActivityIsOpen,
    onCreateActivityDialogClosed,
    person,
    setAndCachePerson,
    openCreateActivityDialog,
    filterName,
    feedbackList,
    renderTeamNav,
    aspirationCreateModalIsOpen,
    toggleAspirationCreateModal,
    aspirations,
    getTabActions,
    showSentFeedback,
    feedbackErrorMessage,
    isAdminable,
    showManagerOnlyPerformanceDetails,
    isGroupAndOptionEnabled,
  ]);

  // if no tab provided in url, and first tab is not resume tab (because it
  // is disabled, then redirect to the first tab's path)
  useEffect(() => {
    // @ts-expect-error
    if (noTabProvidedInUrl && tabs?.[0]?.path !== urlPrefix) {
      // preserve query string/hash (e.g. ?feedback-request-id=) so deep
      // links still work after landing on a non-resume default tab
      history.replace(
        // @ts-expect-error
        tabs?.[0]?.path + location.search + location.hash
      );
    }
  }, [
    history,
    noTabProvidedInUrl,
    tabs,
    urlPrefix,
    location.search,
    location.hash,
  ]);

  const mainPageContent = useMemo(() => {
    // if only one tab, just show that page (without tabs on it),
    // e.g. for ONA-only experience with no resumes and just perf tab, else show tabs
    if (tabs.length === 1) {
      return (
        <Page person={person} title={person?.full_name} subtitle={subtitle}>
          {
            // @ts-expect-error
            tabs[0].action
          }
          {
            // @ts-expect-error
            tabs[0].content
          }
        </Page>
      );
    } else if (tabs.length > 1) {
      return (
        <Page
          person={person}
          title={person?.full_name}
          subtitle={subtitle}
          tabs={tabs}
        />
      );
    } else {
      return (
        <Page person={person} title={person?.full_name} subtitle={subtitle}>
          <Card>
            <CardBody>
              <EmptyState
                title={formatMessage({
                  id: 'app.views.person.person_profile.title.nothing_to_see_here',
                  defaultMessage: 'Nothing to see here',
                })}
                subtitle={formatMessage(
                  {
                    id: 'app.views.person.person_profile.subtitle.nothing_to_see_here',
                    defaultMessage:
                      '{name} has not shared anything with others yet.',
                  },
                  {
                    // @ts-expect-error
                    name: person.given_name,
                  }
                )}
              />
            </CardBody>
          </Card>
        </Page>
      );
    }
  }, [person, subtitle, tabs, formatMessage]);

  if (!validIdentifier) {
    return <Page404 />;
  }

  const loadOrRenderOutput = loadOrRender(person, errorMessage);
  if (loadOrRenderOutput) {
    return loadOrRenderOutput;
  }

  if (typeof person === 'undefined') {
    return <Loading />;
  }

  return mainPageContent;
};

const mapStateToProps = (state) => {
  const { me, currentProxyPerson, currentOrganization, features, authUser } =
    state;

  return {
    me,
    currentProxyPerson,
    currentOrganization,
    features,
    authUser,
  };
};

export default connect(mapStateToProps)(React.memo(PersonProfile));
